/**
 * Phone-first “full screen” chrome for live VoxPont interpreter routes.
 *
 * Hides FusionMSI GlobalTopBarChrome (Settings, Notifications, Help, Account, Apps)
 * so the device looks like a VoxPont-only app. Preference is persisted; desktop is
 * unchanged unless VOXPONT_IMMERSIVE_PREVIEW=1.
 *
 * iOS Safari cannot hide its own URL bar from a tab — only Add to Home Screen can.
 * Android Chrome may hide browser chrome via the Fullscreen API when the user taps
 * Full screen (user gesture required).
 */

import { useCallback, useEffect, useState } from 'react';
import { isLikelyIosDevice } from 'utils/isLikelyIosDevice';

export const VOXPONT_IMMERSIVE_PREVIEW_KEY = 'VOXPONT_IMMERSIVE_PREVIEW';
export const VOXPONT_MOBILE_CHROME_KEY = 'VOXPONT_MOBILE_CHROME';

export const VOXPONT_CHROME_HIDDEN = 'hidden';
export const VOXPONT_CHROME_VISIBLE = 'visible';

/**
 * Live interpreter product routes — not marketing, auth, legal, or the offline
 * download landing. Matching is exact-or-nested so /voxpont2 does not collide
 * with /voxpont20.
 */
export const VOXPONT_INTERPRETER_ROUTES = Object.freeze([
  '/voxpont2',
  '/voxpont3',
  '/voxpont4',
  '/voxpont12',
  '/voxpont5',
  '/voxpont20',
  '/voxpont30',
  '/voxpont40',
  '/voxpont50',
  '/voxpont60',
  '/voxpont70',
  '/voxpont100',
  '/voxpont201',
  '/voxpont202',
  '/voxpont204',
  '/voxpont205',
  '/voxpont301',
  '/voxpont302',
  '/voxpont501',
  '/voxpont700',
]);

const listeners = new Set();

function normalizePath(pathname) {
  if (!pathname) return '';
  const path = String(pathname).split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}

export function isVoxPontInterpreterPath(pathname) {
  const lower = normalizePath(pathname).toLowerCase();
  if (!lower || lower === '/') return false;
  return VOXPONT_INTERPRETER_ROUTES.some((route) => {
    const r = route.toLowerCase();
    return lower === r || lower.startsWith(`${r}/`);
  });
}

export function isLikelyAndroidDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

export function isVoxPontImmersiveDevice() {
  if (isLikelyIosDevice() || isLikelyAndroidDevice()) return true;
  if (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches) {
    return true;
  }
  return false;
}

export function isVoxPontImmersivePreviewEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage?.getItem(VOXPONT_IMMERSIVE_PREVIEW_KEY) === '1';
  } catch {
    return false;
  }
}

export function isVoxPontImmersiveLayoutEnabled() {
  return isVoxPontImmersivePreviewEnabled() || isVoxPontImmersiveDevice();
}

function readChromePreference() {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage?.getItem(VOXPONT_MOBILE_CHROME_KEY);
    if (value === VOXPONT_CHROME_HIDDEN || value === VOXPONT_CHROME_VISIBLE) return value;
    return null;
  } catch {
    return null;
  }
}

export function subscribeVoxPontImmersiveChrome(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyVoxPontImmersiveChrome() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.warn('[VoxPont] immersive chrome listener failed', err);
    }
  });
}

export function setVoxPontChromeHidden(hidden) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(
      VOXPONT_MOBILE_CHROME_KEY,
      hidden ? VOXPONT_CHROME_HIDDEN : VOXPONT_CHROME_VISIBLE,
    );
  } catch (err) {
    console.warn('[VoxPont] could not persist chrome preference', err);
  }
  notifyVoxPontImmersiveChrome();
}

/**
 * Hide FusionMSI chrome on interpreter routes for phones (or preview), unless
 * the user has restored the regular view.
 */
export function shouldHideGlobalTopBarForVoxPont(pathname) {
  if (!isVoxPontInterpreterPath(pathname)) return false;
  if (!isVoxPontImmersiveLayoutEnabled()) return false;
  return readChromePreference() !== VOXPONT_CHROME_VISIBLE;
}

function supportsElementFullscreen() {
  if (typeof document === 'undefined') return false;
  if (isLikelyIosDevice()) return false;
  const el = document.documentElement;
  return Boolean(el.requestFullscreen || el.webkitRequestFullscreen);
}

export async function enterVoxPontBrowserFullscreen() {
  if (!supportsElementFullscreen()) return;
  const el = document.documentElement;
  try {
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
  } catch {
    // Gesture-gated or user-denied; FusionMSI chrome hide still applies.
  }
}

export async function exitVoxPontBrowserFullscreen() {
  if (typeof document === 'undefined') return;
  const active =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    null;
  if (!active) return;
  try {
    if (document.exitFullscreen) await document.exitFullscreen();
    else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
  } catch {
    // Ignore; preference restore still shows FusionMSI chrome.
  }
}

export function useVoxPontImmersiveChrome(pathname) {
  const [, setEpoch] = useState(0);

  useEffect(() => subscribeVoxPontImmersiveChrome(() => setEpoch((n) => n + 1)), []);

  const interpreterPath = isVoxPontInterpreterPath(pathname);
  const eligible = isVoxPontImmersiveLayoutEnabled();
  const hideGlobalChrome = shouldHideGlobalTopBarForVoxPont(pathname);

  const setImmersive = useCallback((immersive, options = {}) => {
    setVoxPontChromeHidden(Boolean(immersive));
    if (immersive && options.requestBrowserFullscreen) {
      void enterVoxPontBrowserFullscreen();
    } else if (!immersive) {
      void exitVoxPontBrowserFullscreen();
    }
  }, []);

  return {
    hideGlobalChrome,
    showRestoreControl: hideGlobalChrome,
    showEnterControl: interpreterPath && eligible && !hideGlobalChrome,
    showSettingsToggle: interpreterPath && eligible,
    setImmersive,
  };
}
